import CameraService, {
	CameraFacingMode,
} from '../../helpers/classes/CameraService';
import { DataStore } from '../../helpers/classes/DataStore';
import EventService from '../../helpers/classes/EventService';
import { ExceptionManager } from '../../helpers/classes/ExceptionManager';
import HttpService from '../../helpers/classes/HttpService';
import LanguageService from '../../helpers/classes/LanguageService';
import RenderingService from '../../helpers/classes/RenderingService';
import SocketService from '../../helpers/classes/SocketService';
import TemplateService from '../../helpers/classes/TemplateService';
import {
	allowItems,
	containsItems,
	removeItems,
} from '../../helpers/functions/arrays';
import IdesMicroHost from '../../ides/host';
import RetinaFactory from '../../retina/factory';
import { DefaultTemplates } from '../configuration/DefaultTemplates';
import { HintType, ReticleType } from '../enums/Camera';
import { FlowState } from '../enums/FlowState';
import { ChangeMode, InputProvider } from '../enums/InputProvider';
import { CapturingMedia } from '../enums/JourneyDefinition';
import { JourneyEvent } from '../enums/JourneyEvent';
import { JourneyState } from '../enums/JourneyState';
import { Templates } from '../enums/Templates';
import { TranslationKey } from '../enums/Translation';
import { IContainerConfiguration, IManualCaptureConfiguration, IPreviewPagesConfiguration } from '../interfaces/ContainerConfiguration';
import { ICropResponse } from '../interfaces/CropResponse';
import { IIdScanData } from '../interfaces/DataFormats';
import { IJourneyState } from '../interfaces/Journey';
import { GatewayStep } from '../renderers/step/gateway';
import { JourneySelectStep } from '../renderers/step/journeySelect';
import { LivenessStep } from '../renderers/step/liveness';
import { LoginStep } from '../renderers/step/login';
import { ResultsStep } from '../renderers/step/results';
import DataService, { DataStoreType } from './DataService';
import { Journey } from './Journey';
import JourneyManager from './JourneyManager';
import { isMobile, isSafari, isIOS } from 'mobile-device-detect';
import { PassiveLivenessStep } from '../renderers/step/passiveLiveness';
import { IJourneyDefinition } from '../interfaces/JourneyDefinition';
import { ScannerProvider } from '../renderers/provider/scanner';
import { resolve } from 'url';
import * as deepmerge from 'deepmerge';
import { DataChecksStep } from '../renderers/step/dataChecks';
import { TermsAndConditionsStep } from '../renderers/step/termsAndConditions';
import StorageService from '../../helpers/classes/StorageService';
import { STORAGE_KEYS } from '../configuration/StorageKeys';

declare var ENV: string;

// tslint:disable: array-type

/**
 * Primary container for the IDScan Journey
 *
 * @public
 */
export class JourneyContainer {
	/**
	 * @internal
	 */
	protected _config: IContainerConfiguration;

	/**
	 * @internal
	 */
	protected _container: HTMLElement = document.createElement('div');

	/**
	 * @internal
	 */
	protected _cropResponse?: ICropResponse;

	/**
	 * @internal
	 */
	protected _journey?: Journey;

	/**
	 * @internal
	 */
	protected _inputProvider?: InputProvider;

	/**
	 * @internal
	 */
	protected _providers: InputProvider[] = [];

	/**
	 * @internal
	 */
	protected _ready: boolean = false;

	/**
	 * @internal
	 */
	protected _dataStore: DataStore<IIdScanData> = DataService.getDataStore<
		IIdScanData
	>(DataStoreType.ID_SCAN);

	/**
	 * @internal
	 */
	protected _journeyDefinitionId!: string;

	protected _previewPages: IPreviewPagesConfiguration;

	protected _manualCapture: IManualCaptureConfiguration;

	protected assetsFolder: string;

	protected _isPredefinedJourney: boolean = false;

	protected _isTCCAccepted: boolean = false;

	protected _retry: boolean = false;

	/**
	 * Primary container for the IDScan Journey
	 */
	constructor(config: IContainerConfiguration) {
		this._config = config;
		this.assetsFolder = this._config.assetsDirectory === undefined ? 'assets' : this._config.assetsDirectory;
		this._previewPages = deepmerge(
			{
				documentStep: {
					smartCapture: false,
					manualCapture: false,
					fileUpload: true
				},
				facematchStep: {
					manualCapture: false,
					fileUpload: true
				},
				poaStep: {
					manualCapture: false,
					fileUpload: true
				},
				passiveLivenessStep: {
					manualCapture: false,
				},
				covidStep: {
					manualCapture: false,
					fileUpload: true
				},
				dataChecksStep: {
					smartCapture: false,
					manualCapture: false,
					fileUpload: false
				}
			}, this._config.previewPages !== undefined
			? this._config.previewPages
			: {});
		this._manualCapture = deepmerge(
			{
				enabled: false,
				timeout: 15
			}, this._config.manualCapture !== undefined
			? this._config.manualCapture
			: {}
		);
		const {
			templates = {},
			translator,
			dictionary,
			overlay,
			cameraOptions,
		} = config;


		TemplateService.updateTemplates({ ...DefaultTemplates, ...templates });
		LanguageService.setTranslator(translator);
		LanguageService.setDictionary(dictionary);
		CameraService.setConfiguration({ overlay, cameraOptions });
	}

	/**
	 * Initialise this instance of the `JourneyContainer` and begin the journey
	 * process for the user.
	 *
	 * @returns The instance of the `JourneyContainer` to chain from
	 */
	public initialize = (): Promise<this> => {
		const { backendUrl, endpoints, container, token, mode, isVerboseLogEnabled, hideLogo } = this._config;
		return this.setContainer(container)
			.then((c) => TemplateService.renderToContainer(c, Templates.Initializing))
			.then(() =>
				JourneyManager.configure({ auth: true, backendUrl, endpoints, mode, isVerboseLogEnabled, hideLogo })
			)
			.then(() => JourneyManager.initialize({ token }))
			.then(() =>
				Promise.all([
					EventService.subscribe(
						JourneyEvent.AUTH_CHANGE,
						this.onAuthStateChanged
					),
					...Object.keys(JourneyEvent).map((key) =>
						EventService.subscribe(
							(JourneyEvent as any)[key],
							this.onJourneyEvent(key as JourneyEvent)
						)
					),
				])
			)
			.then(() => (this._ready = true))
			.then(() => this.evaluate())
			.then(() => this);
	};

	public getJourneyDefinitions = (): Promise<IJourneyDefinition[]> => {
		const { backendUrl, endpoints, token, mode, isVerboseLogEnabled, hideLogo } = this._config;

		return JourneyManager.configure({ auth: true, backendUrl, endpoints, mode, isVerboseLogEnabled, hideLogo })
			.then(() => JourneyManager.initialize({ token }))
			.then(() => JourneyManager.getJourneyDefinitions());
	}

	public setJourneyDefinitonId = (id: string): Promise<void> =>
		new Promise((resolve) => {
			this._config.journeyDefinitionId = id;
			resolve();
		});


	/**
	 * Changes input provider if the provider selected exists
	 *
	 * @param provider - The `InputProvider` to change to if available
	 * @param force - Force the change even if the provider isn't showing as available
	 *
	 * @returns The new input provider
	 *
	 * @internal
	 */
	public changeInputProvider = (
		provider: InputProvider = InputProvider.NONE,
		mode: ChangeMode = ChangeMode.DEFAULT
	): Promise<InputProvider> =>
		new Promise((resolve, reject) => {
			switch (mode) {
				case ChangeMode.DEFAULT:
					return this._providers.indexOf(provider) >= 0
						? resolve((this._inputProvider = provider))
						: reject(
							ExceptionManager.process(
								new ExceptionManager.UnavailableInputProviderException(
									TranslationKey.EXCEPTION_UNAVAILABLE_INPUT_PROVIDER_NOT_AVAILABLE
								)
							)
						);
				case ChangeMode.FALLBACK:
					return resolve(
						(this._inputProvider =
							this._providers.indexOf(provider) >= 0
								? provider
								: this._providers[0])
					);
				case ChangeMode.FORCE:
					return resolve((this._inputProvider = provider));
				default:
					return reject(
						ExceptionManager.process(
							new ExceptionManager.UnavailableInputProviderException(
								TranslationKey.EXCEPTION_UNAVAILABLE_INPUT_PROVIDER_NOT_AVAILABLE
							)
						)
					);
			}
		});

	/**
	 * Terminate this instance of the `JourneyContainer`. This un-subscribes all
	 * event handlers throughout the system then terminates the journey.
	 *
	 * NOTE: To terminate the journey, the `terminateJourney` function is
	 *       automatically called and should not be called beforehand. If you
	 *       only want to terminate the journey, call this method instead.
	 *
	 * @returns Promise to chain from
	 */
	public terminate = () => {
		EventService.terminate()
			.then(() => IdesMicroHost.destroyWorker())
			.then(() => this.terminateJourney());
	};

	/**
	 * Terminate the current journey and halt the retina service currently
	 * associated with it.
	 *
	 * @returns Promise to chain from
	 */
	public terminateJourney = () =>
		SocketService.resetTerminal()
			.catch(() => {
				/* NO-OP */
			})
			.then(() => CameraService.stopStreaming())
			.catch(() => {
				/* NO-OP */
			})
			.then(() => this._journey && this._journey.cancel())
			.then(() => this.clearJourney())
			.then(() => RetinaFactory.terminateService());

	/**
	 * Get the current state of the journey
	 *
	 * @returns The current state of the journey as an `IJourneyState`
	 */
	public getJourneyState = (): IJourneyState => {
		return {
			journey: this._dataStore.getState(),
			inputProvider: this._inputProvider
				? this._inputProvider
				: InputProvider.NONE,
			// TODO: This needs populating
			page: Templates.None,
			...(this._journey
				? this._journey.getState()
				: {
					action: FlowState.NONE,
					actionAttempt: 0,
				}),
		};
	};

	/**
	 * Does the current journey configuration require the use of a camera?
	 *
	 * @remarks This will only review the requirement of a camera for normal
	 * 			document upload, not the use of liveness as it's not always
	 * 			possible to assert this.
	 *
	 * @public
	 */
	public requiresCamera = (): boolean =>
		[
			InputProvider.CAMERA,
			InputProvider.SMART_CAPTURE,
			InputProvider.LIVENESS,
		].indexOf(this._inputProvider!) >= 0;

	/**
	 * Get the allowed file formats as specified by the provided configuration
	 * or the defaults if not provided.
	 *
	 * @internal
	 */
	public getAllowedFileFormats(this: JourneyContainer): string[] {
		var allowedTypes = [
			'image/bmp',
			'image/jpg',
			'image/jpeg',
			'image/png',
			'application/pdf',
		];
		if (this._journey!.action == FlowState.SELFIE) {
			allowedTypes = [
				'image/bmp',
				'image/jpg',
				'image/jpeg',
				'image/png',
			]
		}
		const {
			allowedFileFormats = allowedTypes,
		} = this._config;
		return allowedFileFormats;

	}

	/**
	 * Get a list of the currently available providers
	 */
	public getAvailableProviders = () => [...this._providers];

	/**
	 * Calculates the current state in the journey based on several parameters
	 * and alters the UI accordingly.
	 *
	 * @returns Promise to chain from
	 *
	 * @internal
	 */
	protected evaluate = (isFromTripleScanGuidancePage: boolean = false, isPredefinedJourney: boolean = false, selectedInputProvider: InputProvider = InputProvider.NONE): Promise<void> =>
		this.getJourney()
			.then((journey) => {
				this._isPredefinedJourney = isPredefinedJourney;

				if (ENV === 'DEVELOPMENT') {
					console.log(`********* Current step ${journey.action} *********`);
				}

				if (
					journey.status === JourneyState.ENDED ||
					journey.action === FlowState.LIVENESS ||
					journey.action === FlowState.PASSIVE_LIVENESS ||
					journey.action === FlowState.SELFIE ||
					journey.action === FlowState.ADDRESS ||
					journey.action === FlowState.COVIDPASS ||
					journey.action === FlowState.DATACHECKS
				) {
					return SocketService.resetTerminal()
						.catch(() => {
							/* NO-OP */
						})
						.then(() => journey);
				} else {
					return journey;
				}
			})
			.then((journey) => {
				journey
				if (this._ready === true) {
					if (HttpService.auth === false) {
						return LoginStep.render.call(this);
					} else if (!this._journeyDefinitionId) {
						return JourneySelectStep.render.call(this);
					}
					else if (!this._journeyDefinitionId && !this._journeyDefinitionId) {
						return JourneySelectStep.render.call(this);
					} else {
						if (!this._isTCCAccepted && !journey.isCancelled)
							return TermsAndConditionsStep.render.call(this);

						if (journey.status === JourneyState.ENDED) {
							return ResultsStep.render.call(this);
						} else if (journey.action === FlowState.LIVENESS) {
							return LivenessStep.render
								.call(this)
								.then(() => this.updateJourneyFields())
								.then(() => this.hookGlobalButtons());
						} else if (journey.action === FlowState.PASSIVE_LIVENESS) {
							return PassiveLivenessStep.render
								.call(this)
								.then(() => this.updateJourneyFields())
								.then(() => this.hookGlobalButtons());
						} else if (journey.action === FlowState.DATACHECKS) {
							return DataChecksStep.render
								.call(this)
								.then(() => this.updateJourneyFields())
								.then(() => this.hookGlobalButtons());
						}
						else {
							return GatewayStep.render
								.call(this, isFromTripleScanGuidancePage, isPredefinedJourney, selectedInputProvider)
								.then(() => this.updateJourneyFields())
								.then(() => this.hookGlobalButtons());
						}
					}
				} else {
					return Promise.resolve();
				}
			})
			.catch((e) => {
				if (HttpService.auth === false) {
					return LoginStep.render.call(this);
				} else {
					throw e;
				}
			});

	/**
	 * Clears the current journey and removes associated data
	 *
	 * @returns Promise to chain from
	 *
	 * @internal
	 */
	protected clearJourney = (): Promise<void> => {
		this._journey = undefined;
		this._cropResponse = undefined;
		StorageService.remove(STORAGE_KEYS.journeyId);
		this._dataStore.reset();
		return EventService.publish(JourneyEvent.JOURNEY_CLEAR, {
			step: Templates.Result,
		});
	};

	/**
	 * Creates a journey and assigns it as the main journey in this instance.
	 *
	 * @returns The current journey
	 *
	 * @internal
	 */
	protected getJourney = (): Promise<Journey> => {
		if (this._journey) {
			return Promise.resolve(this._journey);
		} else {
			const _id =
				window && (window as any).jcs__id__box
					? (window as any).jcs__id__box
					: undefined;

			return JourneyManager.createJourney(
				_id,
				this._config.additionalData
			).then((journey) => (this._journey = journey));
		}
	};

	/**
	 * Update the UI to represent the current state of the journey
	 *
	 * @returns Promise<void>
	 *
	 * @internal
	 */
	protected hookGlobalButtons = (isDisplay: boolean = true): Promise<void> =>
		TemplateService.getElements(this._container, {
			cancel: '[data-jcs-element="cancel__journey"]',
		}).then(({ cancel }) => {
			if (cancel instanceof HTMLElement) {

				if (!isDisplay) { RenderingService.hideObject(cancel); }

				cancel.addEventListener('click', () =>
					SocketService.resetTerminal()
						.catch(() => {
							/* NO-OP */
						})
						.then(() => SocketService.release())
						.then(() => CameraService.stopStreaming())
						.catch(() => {
							/* NO-OP */
						})
						.then(() => this._journey && this._journey!.cancel())
						.then(() => RetinaFactory.terminateService())
						.then(() => this.evaluate())
				);
				if (SocketService && SocketService.state === 1)
					return ScannerProvider.startSocket.call(this);
			}
		});

	/**
	 * Update the UI to represent the current state of the journey
	 *
	 * @returns Promise<void>
	 *
	 * @internal
	 */
	protected updateJourneyFields = (): Promise<void> =>
		TemplateService.getElements(this._container, {
			action: '[data-jcs-element="info__journey__action"]',
			actionContainer: '[data-jcs-element="info__journey__action__container"]',
			attempt: '[data-jcs-element="info__journey__action__attempt"]',
			id: '[data-jcs-element="info__journey__id"]',
		}).then(({ id, action, actionContainer, attempt }) => {
			if (this._journey) {
				RenderingService.setText(id, this._journey.id.substr(0, 8));
				RenderingService.setText(attempt, String(this._journey.actionAttempt));
				RenderingService.setText(
					action,
					LanguageService.getTranslation(
						`FLOW_STATE_${this._journey.action}` as TranslationKey
					)
				);
			}
		});

	/**
	 * Change the camera the images are currently being streamed from. If no
	 * deviceId is defined, the first camera will be used.
	 *
	 * @param deviceId - The id of the camera to change the stream for to
	 *
	 * @returns Promise resolving with the deviceId for the camera in use
	 *
	 * @internal
	 */
	protected changeCameraStream = (
		deviceId?: string,
		reticleType: ReticleType = ReticleType.NONE,
		watermark: boolean = false,
		hintType: HintType = HintType.NONE,
		mirror: boolean | undefined = undefined,
		facingMode?: CameraFacingMode
	): Promise<string | undefined> =>
		TemplateService.getElements(this._container, {
			cameraViewfinder: '[data-jcs-element="camera__viewfinder"]',
			status: '[data-jcs-element="view__status"]',
			statusMessage: '[data-jcs-element="view__status__message"]',
		})
			.then(({ cameraViewfinder, status, statusMessage, }) => {
				RenderingService.hideObject(status);
				if (cameraViewfinder instanceof HTMLCanvasElement) {
					return CameraService.stopStreaming()
						.then(() => CameraService.clearCanvas(cameraViewfinder))
						.then(() =>
							EventService.publish(JourneyEvent.CAMERA_CHANGE, {
								camera: deviceId,
							})
						)
						.then(() => CameraService.getStream(deviceId, facingMode))
						.then((stream) =>
							CameraService.renderStreamToElement(stream, cameraViewfinder, {
								reticleType,
								hintType,
								watermark,
								mirror
							})
						).catch((error) => {
							if (error.message == 'EXCEPTION_PLATFORM_CAMERA_NOT_READABLE') {
								// camera stream can not be read, might be in use by another application
								RenderingService.showObject(status as HTMLElement);
								RenderingService.setText(statusMessage as HTMLElement, LanguageService.getTranslation(TranslationKey.EXCEPTION_PLATFORM_CAMERA_NOT_READABLE));
							};
						});
				} else {
					return Promise.resolve();
				}
			})
			.then(() => CameraService.currentDeviceId);

	/**
	 * Freezes the image in the canvas, stopping the streaming.
	 */
	protected freezeCameraStream = (): Promise<void> =>
		TemplateService.getElements(this._container, {
			cameraViewfinder: '[data-jcs-element="camera__viewfinder"]',
		}).then(({ cameraViewfinder }) => {
			if (cameraViewfinder instanceof HTMLCanvasElement) {
				const context = cameraViewfinder.getContext('2d');
				if (context) {
					CameraService.getImageFromCurrentStream().then((data) => {
						const img = new Image();
						img.src = data;
						context.drawImage(img, 0, 0);
					});
				}
				return CameraService.stopStreaming();
			} else {
				return Promise.resolve();
			}
		});

	/**
	 * Checks whether the base64 string is valid or not
	 *
	 * @param base64 - The string to validate
	 *
	 * @internal
	 */
	protected assertImageExists = (base64: string) =>
		new Promise<void>((resolve, reject) => {
			if (
				base64 === undefined ||
				base64 === null ||
				typeof base64 !== 'string'
			) {
				return reject(
					ExceptionManager.process(
						new ExceptionManager.BadRequestException(
							TranslationKey.EXCEPTION_BAD_REQUEST_INVALID_IMAGE
						)
					)
				);
			} else {
				return resolve();
			}
		});

	/**
	 * Renders the currently available cameras into the specified element and
	 * sets the currently used/active camera in the element.
	 *
	 * @param el - HTMLSelectElement to render the camera names into
	 *
	 * @internal
	 */
	protected renderAvailableCameras = (el?: HTMLSelectElement) =>
		CameraService.getVideoCameras()
			.then((cameras) =>
				cameras.map((camera) => ({
					name: camera.label,
					value: camera.deviceId,
					selected: CameraService.currentDeviceId === camera.deviceId,
				}))
			)
			.then((cameras) => {
				RenderingService.updateSelect(el, cameras);
				if (cameras && cameras.length > 1) {
					RenderingService.showObject(el);
				} else {
					RenderingService.hideObject(el);
				}
			})

	/**
	 * Sets the available providers
	 *
	 * @returns Promise for chaining
	 *
	 * @internal
	 */
	protected setProviders = (
		media: CapturingMedia,
		isPredefinedJourney: boolean = false
	): Promise<Array<InputProvider>> =>
		new Promise((resolve, reject) => {
			const providers = this.parseProvidersFromCapturingMedia(media, isPredefinedJourney);
			return Promise.resolve()
				.then(() => {
					const {
						allowedInputProviders = Object.keys(InputProvider),
					} = this._config;

					return allowItems(providers, allowedInputProviders);
				})
				.then(() =>
					!!(window as any).Worker
						? providers
						: removeItems(providers, [InputProvider.SMART_CAPTURE])
				)
				.then(() =>
					!!(window as any).WebAssembly
						? providers
						: removeItems(providers, [InputProvider.SMART_CAPTURE])
				)
				.then(
					(): Promise<Array<InputProvider>> => {
						if (
							containsItems(providers, [
								InputProvider.CAMERA,
								InputProvider.SMART_CAPTURE,
								InputProvider.LIVENESS,
							])
						) {
							return CameraService.doDeviceProbe().then(() => {
								if (CameraService.hasDeviceCapableOfResolution()) {
									return providers;
								} else {
									return removeItems(providers, [
										InputProvider.CAMERA,
										InputProvider.SMART_CAPTURE,
									]);
								}
							});
						} else {
							return Promise.resolve(providers);
						}
					}
				)
				.then(() => {
					if (providers.length) {
						return resolve((this._providers = providers));
					} else {
						return reject(
							ExceptionManager.process(
								new ExceptionManager.UnavailableInputProviderException(
									TranslationKey.EXCEPTION_UNAVAILABLE_INPUT_PROVIDER_NONE_SPECIFIED
								)
							)
						);
					}
				})
				.catch(() =>
					reject(
						ExceptionManager.process(
							new ExceptionManager.UnavailableInputProviderException(
								TranslationKey.EXCEPTION_UNAVAILABLE_INPUT_PROVIDER_CANNOT_RETRIEVE
							)
						)
					)
				);
		});

	/**
	 * Trigger proxy for the journey event callback
	 *
	 * @param key - The key to report on
	 *
	 * @returns Function for passing event data into with the referenced key as the first parameter
	 *
	 * @internal
	 */
	private onJourneyEvent = (key: JourneyEvent) => (eventData: any) => {
		const {
			onJourneyEvent = () => {
				/* NO-OP */
			},
		} = this._config;

		onJourneyEvent(
			(JourneyEvent as any)[key],
			eventData,
			this.getJourneyState()
		);
	};

	/**
	 * Set the container to use for the lifetime of this instance.
	 *
	 * NOTE: This should not be changed once the instance has been initialised
	 *       as references to objects will be destroyed. If the component needs
	 *       to be changed, the `JourneyContainer` should be terminated and
	 *       reinitialized instead.
	 *
	 * @returns Promise resolving with the specified container for chaining
	 *
	 * @internal
	 */
	private setContainer = (
		container: string | HTMLElement
	): Promise<HTMLElement> =>
		new Promise((resolve, reject) => {
			if (typeof container === 'string') {
				const _container = RenderingService.getElementBySelector(container);

				if (_container instanceof HTMLElement) {
					_container.setAttribute('wsdk-container', '');
					return resolve((this._container = _container));
				} else {
					return reject(
						ExceptionManager.process(
							new ExceptionManager.TemplateException(
								TranslationKey.EXCEPTION_TEMPLATE_CONTAINER_MISSING
							)
						)
					);
				}
			} else if (container instanceof HTMLElement) {
				container.setAttribute('wsdk-container', '');
				return resolve((this._container = container));
			} else {
				return reject(
					ExceptionManager.process(
						new ExceptionManager.InvalidParameterException(
							TranslationKey.EXCEPTION_INVALID_PARAMETER_SHOULD_BE_ELEMENT_OR_SELECTOR
						)
					)
				);
			}
		});
	/**
	 * Activates the new auth state and reinitializes the rendering process.
	 *
	 * @param state - Object containing the new `authorized` state to use
	 *
	 * @returns Promise for chaining
	 *
	 * @internal
	 */
	private onAuthStateChanged = ({ authorized }: { authorized: boolean }) =>
		this.evaluate().catch(() =>
			console.error('onAuthStateChanged', authorized)
		);

	/**
	 * @param media - The CapturingMedia value to use
	 *
	 * @internal
	 */
	private parseProvidersFromCapturingMedia = (media: CapturingMedia, isPredefinedJourney: boolean = false) => {
		switch (media) {
			case CapturingMedia.FileUpload:
				return [InputProvider.FILESYSTEM];

			case CapturingMedia.Camera:
				return [InputProvider.CAMERA, InputProvider.FILESYSTEM];

			case CapturingMedia.WebCamera:
				{
					if (isPredefinedJourney) {
						return [
							InputProvider.SMART_CAPTURE,
							InputProvider.CAMERA,
						]
					}
					else {
						return [
							InputProvider.SMART_CAPTURE,
							InputProvider.CAMERA,
							InputProvider.FILESYSTEM,
						]
					}
				};

			case CapturingMedia.Scanner:
				return [InputProvider.SCANNER, InputProvider.FILESYSTEM, InputProvider.CAMERA];

			default:
				return [];
		}
	};
}
